import type { TFunction } from 'i18next';
import {
  defaultEmbeddedIframeSandbox,
  defaultEmbeddedToolbarActions,
} from '@/constants/embeddedApplication';
import type { MenuItem } from '@/types/menu';
import type { Product } from '@/types/product';
import type {
  EmbeddedApplication,
  EmbeddedNavigationGroup,
  EmbeddedNavigationItem,
} from '@/types/embeddedApplication';

interface BuildEmbeddedApplicationParams {
  product: Product;
  menuItems?: MenuItem[];
  selectedMenuItemId?: string;
  t: TFunction;
  portalLabel?: string;
  navigationGroups?: EmbeddedNavigationGroup[];
  toolbarActions?: EmbeddedApplication['toolbarActions'];
}

function mapMenuItemToNavigationItem(
  menuItem: MenuItem,
): EmbeddedNavigationItem {
  return {
    id: menuItem.id,
    label: menuItem.label,
    iframeUrl: menuItem.iframeUrl,
    route: menuItem.route,
    visible: true,
  };
}

function buildDefaultNavigationGroups(
  menuItems: MenuItem[],
  defaultGroupLabel: string,
): EmbeddedNavigationGroup[] {
  if (menuItems.length === 0) {
    return [];
  }

  return [
    {
      id: `${menuItems[0]?.productId ?? 'app'}-navigation`,
      label: defaultGroupLabel,
      items: menuItems.map(mapMenuItemToNavigationItem),
      collapsible: true,
      defaultExpanded: true,
      visible: true,
    },
  ];
}

function resolveSelectedMenuItem(
  menuItems: MenuItem[],
  selectedMenuItemId?: string,
): MenuItem | undefined {
  if (!menuItems.length) {
    return undefined;
  }

  return (
    menuItems.find((item) => item.id === selectedMenuItemId) ?? menuItems[0]
  );
}

function flattenNavigationItems(
  groups: EmbeddedNavigationGroup[],
): EmbeddedNavigationItem[] {
  const collect = (items: EmbeddedNavigationItem[]): EmbeddedNavigationItem[] =>
    items.flatMap((item) => [item, ...collect(item.children ?? [])]);

  return groups.flatMap((group) => collect(group.items));
}

export function buildEmbeddedApplication({
  product,
  menuItems = [],
  selectedMenuItemId,
  t,
  portalLabel,
  navigationGroups,
  toolbarActions,
}: BuildEmbeddedApplicationParams): EmbeddedApplication {
  const selectedMenuItem = resolveSelectedMenuItem(
    menuItems,
    selectedMenuItemId,
  );
  const resolvedPortalLabel = portalLabel ?? t('embedded.embeddedPortalLabel');
  const navigation =
    navigationGroups ??
    buildDefaultNavigationGroups(
      menuItems,
      t('embedded.embeddedNavigationDefaultGroup'),
    );
  const selectedNavigationItem =
    flattenNavigationItems(navigation).find(
      (item) => item.id === selectedMenuItemId,
    ) ?? flattenNavigationItems(navigation)[0];

  const breadcrumbs = [
    { label: resolvedPortalLabel },
    { label: t(product.name, { defaultValue: product.name }) },
  ];

  if (selectedNavigationItem || selectedMenuItem) {
    breadcrumbs.push({
      label: t(selectedNavigationItem?.label ?? selectedMenuItem?.label ?? '', {
        defaultValue:
          selectedNavigationItem?.label ?? selectedMenuItem?.label ?? '',
      }),
    });
  }

  return {
    id: product.id,
    name: t(product.name, { defaultValue: product.name }),
    icon: product.icon ?? undefined,
    iframeUrl: product.iframeUrl,
    navigation,
    toolbarActions: toolbarActions ?? defaultEmbeddedToolbarActions,
    breadcrumbs,
    sandbox: defaultEmbeddedIframeSandbox,
    allowFullscreen: true,
  };
}
